import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';

import { ApiError, unwrap } from '@/api/envelope';
import {
  publish as publishRequest,
  saveDraft as saveDraftRequest,
  storeArcImage,
  unpublish as unpublishRequest,
  type ArcDraftUpdate,
} from '@/api/generated';
import { useReadOnly } from '@/auth/useViewingAs';

import { serverSentence as reason, type Arc } from './arcTypes';
import type { PendingImage } from './draftEdits';
import { arcKey, arcVersionKey } from './keys';
import { useArcOrganizationId } from './useArc';
import { versionOf } from './useArcVersion';

/**
 * Staff viewing an organization may not change it. The request interceptor
 * refuses such a write anyway; this stops it before it is sent.
 */
const VIEWING_REFUSAL = 'Nothing can be changed while viewing an organization.';

/** Someone else saved after this copy was loaded: who, and when (B30, D28). */
export interface Conflict {
  savedBy: string | null;
  savedAt: string | null;
}

/**
 * The backend's out-of-date 409 (arc-and-discovery-hardening, B10, B30), told
 * apart from the ARC's other 409s by the current version it carries.
 */
function conflictFrom(error: unknown): Conflict | null {
  if (!(error instanceof ApiError) || error.status !== 409) return null;
  if (typeof error.data !== 'object' || error.data === null) return null;
  const { current_version, draft_saved_at, draft_saved_by } = error.data as Record<
    string,
    unknown
  >;
  if (typeof current_version !== 'number') return null;
  // Read by type, not trusted by it (review item 42): a name or time that is
  // not text reads as unknown, rather than breaking the words built from it.
  return {
    savedBy: typeof draft_saved_by === 'string' ? draft_saved_by : null,
    savedAt: typeof draft_saved_at === 'string' ? draft_saved_at : null,
  };
}

/** The save that runs before a publish was refused; the publish never ran. */
class SaveStepRefused extends Error {
  constructor(readonly refusal: unknown) {
    super('The draft was not saved, so it was not published.');
  }
}

/**
 * The ARC Builder's writes (Day 26, Save Draft; group 6, Unpublish).
 *
 * Every edit on the page changes the working copy only (`useDraft`); these
 * are the requests that reach the server. Save Draft sends the whole copy in
 * one request and writes the reply - the whole ARC - into the cache, so the
 * saved draft and the page agree at once. Storing an image changes nothing on
 * the ARC: the copy names the key, and the next save makes it the draft's.
 * Publish saves first when there are unsaved changes, then publishes.
 * Unpublish takes the ARC out of the app and keeps the draft (group 6, D8).
 * A refused save, publish or unpublish keeps its sentence under the bar until
 * the next try.
 *
 * `onSaved` runs after every saved draft, the one Publish sends included, so
 * the page drops its working copy for the saved one even when the publish
 * that followed was refused.
 */
export function useArcMutations(onSaved?: () => void) {
  const queryClient = useQueryClient();
  const readOnly = useReadOnly();
  const organizationId = useArcOrganizationId();
  const key = arcKey(organizationId);
  const [saveRefusal, setSaveRefusal] = useState<string | null>(null);
  const [publishRefusal, setPublishRefusal] = useState<string | null>(null);
  const [unpublishRefusal, setUnpublishRefusal] = useState<string | null>(null);
  // A save or publish from an out-of-date copy: shown as its own screen, not a refusal.
  const [conflict, setConflict] = useState<Conflict | null>(null);

  /** Records an out-of-date refusal as the conflict; false for any other failure. */
  const asConflict = (error: unknown) => {
    const found = conflictFrom(error);
    if (found) setConflict(found);
    return found !== null;
  };

  const store = (arc: Arc) => {
    queryClient.setQueryData(key, arc);
    // The version check learns of this page's own write at once, so it never
    // reads it as someone else's (arc-and-discovery-hardening, B38).
    queryClient.setQueryData(arcVersionKey(organizationId), versionOf(arc));
  };

  const send = async (body: ArcDraftUpdate): Promise<Arc> => {
    if (readOnly) throw new Error(VIEWING_REFUSAL);
    const reply = await saveDraftRequest({ body, throwOnError: true });
    const saved = unwrap(reply.data);
    store(saved);
    onSaved?.();
    return saved;
  };

  const saveDraft = useMutation({
    mutationFn: send,
    onMutate: () => {
      setSaveRefusal(null);
      setPublishRefusal(null);
      setUnpublishRefusal(null);
    },
    onError: (error) => {
      if (asConflict(error)) return;
      setSaveRefusal(reason(error, 'The draft could not be saved. Please try again.'));
    },
  });

  /** Stores an image for the copy; a refusal is thrown, for the caller to show. */
  const uploadImage = async (
    purpose: 'logo' | 'cover' | 'inline',
    file: File,
  ): Promise<PendingImage> => {
    if (readOnly) throw new Error(VIEWING_REFUSAL);
    try {
      const reply = await storeArcImage({
        query: { purpose },
        body: { file },
        throwOnError: true,
      });
      const stored = unwrap(reply.data);
      return { key: stored.key, url: stored.url ?? null };
    } catch (error) {
      throw new Error(reason(error, 'The image could not be stored. Please try again.'));
    }
  };

  /**
   * Saves `unsaved` first when given, then publishes from `version`, the
   * version the page shows - or, after that save, the one it answered
   * (arc-and-discovery-hardening, B10).
   */
  const publish = useMutation({
    mutationFn: async ({
      unsaved,
      version,
    }: {
      unsaved: ArcDraftUpdate | null;
      version: number;
    }) => {
      let expected = version;
      if (unsaved) {
        try {
          expected = (await send(unsaved)).draft_version;
        } catch (error) {
          // The draft was refused, not the publish: said under Save, where
          // the unsaved-changes chip still stands, rather than as a publish
          // problem the person then cannot find.
          if (!asConflict(error)) {
            setSaveRefusal(
              reason(error, 'The draft could not be saved. Please try again.'),
            );
          }
          throw new SaveStepRefused(error);
        }
      }
      if (readOnly) throw new Error(VIEWING_REFUSAL);
      const reply = await publishRequest({
        body: { expected_version: expected },
        throwOnError: true,
      });
      return unwrap(reply.data);
    },
    onMutate: () => {
      setSaveRefusal(null);
      setPublishRefusal(null);
      setUnpublishRefusal(null);
    },
    onSuccess: store,
    onError: (error) => {
      if (error instanceof SaveStepRefused || asConflict(error)) return;
      setPublishRefusal(
        reason(error, 'The ARC could not be published. Please try again.'),
      );
    },
  });

  /**
   * Takes the published copy out of the app (group 6, D8, B13). The draft
   * and the last published time stay; the reply is the whole ARC, stored as
   * a publish's is.
   */
  const unpublish = useMutation({
    mutationFn: async () => {
      if (readOnly) throw new Error(VIEWING_REFUSAL);
      const reply = await unpublishRequest({ throwOnError: true });
      return unwrap(reply.data);
    },
    onMutate: () => {
      setSaveRefusal(null);
      setPublishRefusal(null);
      setUnpublishRefusal(null);
    },
    onSuccess: store,
    onError: (error) => {
      setUnpublishRefusal(
        reason(error, 'The ARC could not be unpublished. Please try again.'),
      );
    },
  });

  return {
    saveDraft,
    uploadImage,
    publish,
    unpublish,
    /** The server's sentence for the last refused save, until the next try. */
    saveRefusal,
    /** The server's sentence for the last refused publish, until the next try. */
    publishRefusal,
    /** The server's sentence for the last refused unpublish, until the next try. */
    unpublishRefusal,
    /** Someone else saved after this copy was loaded, until the latest is loaded. */
    conflict,
    clearConflict: () => setConflict(null),
  };
}

/** Everything `useArcMutations` gives the page and its dialogs. */
export type ArcWrites = ReturnType<typeof useArcMutations>;
